import { Copy, ExternalLink, Package } from 'lucide-react'
import { useState } from 'react'
import { SiShopify } from 'react-icons/si'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'

interface ShopifyProduct {
  id: string
  title: string
  handle: string
  vendor?: string | null
  productType?: string | null
  status: 'ACTIVE' | 'ARCHIVED' | 'DRAFT'
  url: string
}

interface ShopifyProductsListProps {
  products: ShopifyProduct[]
  shopDomain?: string
  hasNextPage?: boolean
  showCount?: boolean
}

async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text)
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-700 border-green-200'
    case 'DRAFT':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200'
    case 'ARCHIVED':
      return 'bg-gray-100 text-gray-700 border-gray-200'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return '🟢'
    case 'DRAFT':
      return '🟡'
    case 'ARCHIVED':
      return '⚫'
    default:
      return '⚪'
  }
}

export function ShopifyProductsList({
  products,
  shopDomain,
  hasNextPage = false,
  showCount = true,
}: ShopifyProductsListProps) {
  const [showDetails, setShowDetails] = useState(false)

  if (products.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4">
        <Package className="h-5 w-5 text-gray-400" />
        <span className="text-sm text-gray-600">No products found</span>
      </div>
    )
  }

  const contextActions: ContextAction[] = [
    {
      id: 'copy-all-ids',
      label: 'Copy All Product IDs',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(products.map((p) => p.id).join('\n'))
      },
    },
    {
      id: 'copy-all-handles',
      label: 'Copy All Product Handles',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(products.map((p) => p.handle).join('\n'))
      },
    },
    {
      id: 'copy-all-titles',
      label: 'Copy All Product Titles',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        await copyToClipboard(products.map((p) => p.title).join('\n'))
      },
    },
    ...(shopDomain
      ? [
          {
            id: 'open-products-admin',
            label: 'Open Products in Shopify Admin',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => {
              window.open(`https://${shopDomain}/admin/products`, '_blank', 'noopener,noreferrer')
            },
          },
        ]
      : []),
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="space-y-3">
        {showCount && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SiShopify className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-gray-900">
                {products.length} Product{products.length === 1 ? '' : 's'}
                {hasNextPage && ' (more available)'}
              </span>
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
              <span className={`transform transition-transform ${showDetails ? 'rotate-90' : ''}`}>
                ▶
              </span>
            </button>
          </div>
        )}

        <div className="grid gap-2">
          {products.map((product) => (
            <div
              key={product.id}
              className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h4 className="truncate font-medium text-gray-900">{product.title}</h4>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getStatusColor(product.status)}`}
                    >
                      <span className="mr-1">{getStatusIcon(product.status)}</span>
                      {product.status}
                    </span>
                  </div>

                  <div className="space-y-0.5 text-xs text-gray-500">
                    <div>
                      Handle: <span className="font-mono">{product.handle}</span>
                    </div>
                    {product.vendor && (
                      <div>
                        Vendor: <span className="text-gray-700">{product.vendor}</span>
                      </div>
                    )}
                    {product.productType && (
                      <div>
                        Type: <span className="text-gray-700">{product.productType}</span>
                      </div>
                    )}
                    {showDetails && (
                      <div>
                        ID: <span className="font-mono">{product.id}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ml-3 flex items-center gap-1">
                  <button
                    onClick={() => copyToClipboard(product.handle)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    title="Copy handle"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                  {product.url && (
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600"
                      title="View product"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasNextPage && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <span>📄</span>
              <span>More products available. Use pagination to fetch additional results.</span>
            </div>
          </div>
        )}
      </div>
    </ContextMenuWrapper>
  )
}
